import React, { useState, useEffect } from 'react';
import { useAPI } from 'api';
import { LicenseKey } from 'types';

const LicenseKeys = () => {
	const [licenseKeys, setLicenseKeys] = useState<LicenseKey[]>([]);
	const [newKey, setNewKey] = useState('');
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [actionLoading, setActionLoading] = useState(false);
	const api = useAPI();

	useEffect(() => {
		fetchLicenseKeys();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchLicenseKeys = async () => {
		try {
			setLoading(true);
			setError(null);
			const response = await api.get<LicenseKey[]>('/api/system/licensing/keys');
			setLicenseKeys(response || []);
		} catch (err) {
			setError((err as Error).message || 'Failed to fetch license keys');
			console.error('Error fetching license keys:', err);
		} finally {
			setLoading(false);
		}
	};

	const addLicenseKey = async () => {
		if (!newKey.trim()) {
			setError('Please enter a license key');
			return;
		}

		try {
			setActionLoading(true);
			setError(null);
			await api.post('/api/system/licensing/keys/add', { key: newKey.trim() });
			setNewKey('');
			await fetchLicenseKeys();
		} catch (err) {
			setError((err as Error).message || 'Failed to add license key');
			console.error('Error adding license key:', err);
		} finally {
			setActionLoading(false);
		}
	};

	const removeLicenseKey = async (keyId: string) => {
		try {
			setActionLoading(true);
			setError(null);
			await api.delete(`/api/system/licensing/keys/${keyId}`);
			await fetchLicenseKeys();
		} catch (err) {
			setError((err as Error).message || 'Failed to remove license key');
			console.error('Error removing license key:', err);
		} finally {
			setActionLoading(false);
		}
	};

	const clearAllKeys = async () => {
		if (!window.confirm('Are you sure you want to remove all license keys? This will deauthorize all keys from the licensing server.')) {
			return;
		}

		try {
			setActionLoading(true);
			setError(null);
			await api.delete('/api/system/licensing/keys');
			await fetchLicenseKeys();
		} catch (err) {
			setError((err as Error).message || 'Failed to clear all license keys');
			console.error('Error clearing license keys:', err);
		} finally {
			setActionLoading(false);
		}
	};

	const refreshKeys = async () => {
		try {
			setActionLoading(true);
			setError(null);
			await api.post('/api/system/licensing/keys/refresh', {});
			await fetchLicenseKeys();
		} catch (err) {
			setError((err as Error).message || 'Failed to refresh license keys');
			console.error('Error refreshing license keys:', err);
		} finally {
			setActionLoading(false);
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			addLicenseKey();
		}
	};

	if (loading) {
		return <div>Loading license keys...</div>;
	}

	return (
		<div>
			{error && (
				<div>
					{error}
					<button onClick={() => setError(null)} className="button.link">
						×
					</button>
				</div>
			)}

			<div className="add-key-section">
				<div className="input-group">
					<input
						type="text"
						value={newKey}
						onChange={(e) => setNewKey(e.target.value)}
						onKeyPress={handleKeyPress}
						placeholder="Enter license key (e.g., FV-XYZ-XXXXXX-XXXXXX-XXXXXX-XXXXXX)"
						className="key-input"
						disabled={actionLoading}
					/>
					<button
						onClick={addLicenseKey}
						disabled={!newKey.trim() || actionLoading}
						className="add-button"
					>
						{actionLoading ? 'Adding...' : 'Add Key'}
					</button>
				</div>
			</div>

			<div className="keys-list">
				{licenseKeys.length === 0 ? (
					<div className="no-keys">
						<p>No license keys configured</p>
						<p className="help-text">Add a license key above to get started</p>
					</div>
				) : (
					<>
						<div className="keys-header">
							<h4>Configured Keys ({licenseKeys.length})</h4>
							<div className="keys-actions">
								<button
									onClick={refreshKeys}
									disabled={actionLoading}
									className="refresh-button"
									title="Refresh all license keys with the licensing server"
								>
									{actionLoading ? 'Refreshing...' : 'Refresh All'}
								</button>
								<button
									onClick={clearAllKeys}
									disabled={actionLoading}
									className="clear-all-button"
									title="Remove all license keys and deauthorize them"
								>
									{actionLoading ? 'Clearing...' : 'Clear All'}
								</button>
							</div>
						</div>

						{licenseKeys.map((key) => (
							<div key={key.id} className="key-item">
								<div className="key-info">
									<span className="key-value">{key.key}</span>
									<div className="key-details">
										<span className={`key-status ${key.status}`}>
											{key.status}
										</span>
										{key.type && (
											<span className="key-type">{key.type}</span>
										)}
										{key.expiresAt && (
											<span className="key-expiry">
												Expires: {new Date(key.expiresAt).toLocaleDateString()}
											</span>
										)}
										{key.error && (
											<div className="key-error">
												<strong>Error:</strong> {key.error}
											</div>
										)}
									</div>
								</div>
								<button
									onClick={() => removeLicenseKey(key.id)}
									disabled={actionLoading}
									className="remove-button"
									title="Remove this license key and deauthorize it"
								>
									Remove
								</button>
							</div>
						))}
					</>
				)}
			</div>
		</div>
	);
};

export default LicenseKeys;
